import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/data/site";

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const next = () => setIdx((v) => (v + 1) % TESTIMONIALS.length);
  const prev = () => setIdx((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const t = TESTIMONIALS[idx];

  return (
    <section data-testid="testimonials-section" className="relative py-24 md:py-40 bg-brand-beige border-t border-brand-border overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">(07) — Testimonials</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium">
              Words from <span className="italic text-brand-brown">the room</span>.
            </h2>
            <div className="mt-10 flex items-center gap-4">
              <button
                data-testid="testimonial-prev"
                onClick={prev}
                className="w-12 h-12 rounded-full border border-brand-border flex items-center justify-center hover:bg-brand-gold hover:text-brand-chocolate transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="testimonial-next"
                onClick={next}
                className="w-12 h-12 rounded-full border border-brand-border flex items-center justify-center hover:bg-brand-gold hover:text-brand-chocolate transition-colors"
                aria-label="Next"
              >
                <ChevronRight size={18} />
              </button>
              <span className="ml-4 text-sm text-brand-charcoal/50 font-mono">
                {String(idx + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          <div className="lg:col-span-8 relative min-h-[300px]">
            <Quote size={72} className="absolute -top-6 -left-2 text-brand-brown/15" />
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                data-testid="testimonial-card"
              >
                <div className="flex gap-1 mb-6">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={18} className="fill-brand-gold text-brand-gold" />
                  ))}
                </div>
                <p className="font-display italic text-3xl md:text-4xl lg:text-5xl leading-[1.2] text-brand-charcoal text-balance">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-10 flex items-center gap-4">
                  <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-brand-border" />
                  <div>
                    <p className="font-semibold text-brand-charcoal" data-testid="testimonial-name">{t.name}</p>
                    <p className="text-sm text-brand-charcoal/60">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
